/* eslint-disable complexity */
import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DeliveryChannelPreferences } from 'src/app/shared/models/MFAPreferencesResponse';
import { PinRequest } from 'src/app/shared/services/mfa-pin/APIResponse';
import { MfaPinService } from 'src/app/shared/services/mfa-pin/mfa-pin.service';
import { Configuration, ConfigurationService, Locale, LocaleService } from '@ciam/library';

export enum Keyboard {
  LEFT_ARROW = 'ArrowLeft',
  LEFT = 'Left',
  RIGHT_ARROW = 'ArrowRight',
  RIGHT = 'Right',
  UP_ARROW = 'ArrowUp',
  UP = 'Up',
  DOWN_ARROW = 'ArrowDown',
  DOWN = 'Down',
  BACKSPACE = 'Backspace',
  TAB = 'Tab',
  SHIFT = 'Shift',
  DELETE = 'Delete',
  DEL = 'Del'
}

@Component({
  selector: 'wpro-otp-form',
  templateUrl: './otp-form.component.html',
  styleUrls: ['./otp-form.component.css'],
  standalone: false
})
export class OtpFormComponent implements OnInit, AfterViewInit{
  @Input() method: string;
  @Input() value: string;
  @Input() useDiffMethodLink: boolean;
  @Output() apiSuccess = new EventEmitter<boolean>();
  @Output() changeChannel = new EventEmitter<DeliveryChannelPreferences>();
  verificationCodeForm: UntypedFormGroup;
  toggleElement = false;
  formSubmit = false;
  digitsStatus = {
    digit1: false,
    digit2: false,
    digit3: false,
    digit4: false,
    digit5: false,
    digit6: false
  };
  digits = Object.keys(this.digitsStatus);
  isIEBrowser = /msie\s|trident\/|edge\//i.test(window.navigator.userAgent);
  cursorClicked = false;
  @ViewChild('submitBtn', { static: true }) submitBtnElement: ElementRef;
  @ViewChild('verificationFormInvalid') verificationFormInvalid: ElementRef;
  expiredAndIncorrectError = false;
  systemUnavailableError = false;
  digitValidStatus = false;
  customerSupportUrl: string;
  private readonly host: string;
  public configData: Configuration;
  private locale: Locale;

  constructor(
    private configService: ConfigurationService,
    private localeService: LocaleService,
    private fb: UntypedFormBuilder,
    private el: ElementRef,
    private cdr: ChangeDetectorRef,
    private sendPinService: MfaPinService) {
    this.localeService.watch$().subscribe({
      next: locale => this.locale = locale
    });
    this.configService.clone$().subscribe({
      next: values => this.configData = values
    });
    }

  ngOnInit(): void {
    this.verificationCodeForm = this.fb.group({
      digit1: ['', Validators.required],
      digit2: ['', Validators.required],
      digit3: ['', Validators.required],
      digit4: ['', Validators.required],
      digit5: ['', Validators.required],
      digit6: ['', Validators.required]
    });
  }

  ngAfterViewInit(): void {
    const inputElements = this.el.nativeElement.querySelectorAll('input[type=number]');
    inputElements[0]?.focus();
      }

  validatePin(): void {
    this.formSubmit = true;
    this.expiredAndIncorrectError = false;
    if (this.verificationCodeForm.valid) {
      this.onValidForm();
    } else {
      /***---- If form not valid, mark all inputs as touched  ----**/
      Object.keys(this.verificationCodeForm.controls).forEach((key) => {
        this.verificationCodeForm.controls[key].markAllAsTouched();
        this.toggleElement = true;
      });
    }
  }

  onValidForm(): void {
    const allDigitValue = [];
    const inputElements = this.el.nativeElement.querySelectorAll('input[type=number]');
    inputElements.forEach((input) => {
      allDigitValue.push(input.value);
    });
    const pinInput = allDigitValue.join('');
    const pinRequest: PinRequest = {
      pin: pinInput
    };

    /* TODO: Use FR SDK from step-up.service callback and state. */
    this.sendPinService.verifyPin(pinRequest).subscribe(() => {
      this.apiSuccess.emit();
    }, (httpError) => {
      this.formSubmit = false;
      this.handleValidatePinError(httpError);
    });
  }

  handleValidatePinError(error: HttpErrorResponse): void {
    this.cdr.markForCheck();
    this.verificationCodeForm.reset();
    const badRequest = 400;
    error.status === badRequest ? this.expiredAndIncorrectError = true : this.systemUnavailableError = true;
  }

  resendPin(): void {
    const pinRequest: PinRequest = {
      deliveryMethod: this.method,
      preferredDeliveryType: (this.method === 'EMAIL') ? 'PERSONALEMAIL' : 'PHONE'
    };
    this.expiredAndIncorrectError = false;
    this.systemUnavailableError = false;

    /* TODO: Use FR SDK from step-up.service callback and state. */
    this.sendPinService.requestPin(pinRequest).subscribe(() => {
      this.verificationCodeForm.reset();
      this.formSubmit = false;
    }, () => {});
  }

  focusNextInputBox(event: KeyboardEvent, index: number): void {
    let keyBoard: any = {};
    if (event.code === undefined || event.code === '' || !event.code) {
      keyBoard = this.getKeyBoardEventCode(event);
    } else {
      keyBoard = this.getKeyBoardEvent(event);
    }
    const inputElements = this.el.nativeElement.querySelectorAll('input[type=number]');
    if ((index < inputElements.length - 1 && !this.cursorClicked) || (!this.cursorClicked && keyBoard.isRightArrow)) {
      inputElements[Number(index) + 1].focus();
      inputElements[Number(index) + 1].select();
      inputElements.forEach((val, i) => {
        if (i < index && !val.value) {
          this.digitsStatus[`digit${ Number(i) + 1 }`] = true;
        }
      });
    } else if (index === inputElements.length - 1 && !keyBoard.isRightArrow) {
      this.submitBtnElement.nativeElement.focus();
    } else {
      this.onKeyPress(keyBoard, index);
    }
  }

  getKeyBoardEventCode(event: KeyboardEvent): any {
    return {
      isLeftArrow: this.isIEBrowser ? event.key === Keyboard.LEFT : event.key === Keyboard.LEFT_ARROW,
      isRightArrow: this.isIEBrowser ? event.key === Keyboard.RIGHT : event.key === Keyboard.RIGHT_ARROW,
      isUpArrow: this.isIEBrowser ? event.key === Keyboard.UP : event.key === Keyboard.UP_ARROW,
      isDownArrow: this.isIEBrowser ? event.key === Keyboard.DOWN : event.key === Keyboard.DOWN_ARROW,
      isBackSpace: this.isIEBrowser ? event.key === Keyboard.BACKSPACE : event.key === Keyboard.BACKSPACE,
      isTab: this.isIEBrowser ? event.key === Keyboard.TAB : event.key === Keyboard.TAB,
      isShift: this.isIEBrowser ? event.key === Keyboard.SHIFT : event.key === Keyboard.SHIFT,
      isDelete: this.isIEBrowser ? event.key === Keyboard.DEL : event.key === Keyboard.DELETE
    };
  }

  getKeyBoardEvent(event: KeyboardEvent): any {
    return {
      isLeftArrow: this.isIEBrowser ? event.key === Keyboard.LEFT : event.code === Keyboard.LEFT_ARROW,
      isRightArrow: this.isIEBrowser ? event.key === Keyboard.RIGHT : event.code === Keyboard.RIGHT_ARROW,
      isUpArrow: this.isIEBrowser ? event.key === Keyboard.UP : event.code === Keyboard.UP_ARROW,
      isDownArrow: this.isIEBrowser ? event.key === Keyboard.DOWN : event.code === Keyboard.DOWN_ARROW,
      isBackSpace: this.isIEBrowser ? event.key === Keyboard.BACKSPACE : event.code === Keyboard.BACKSPACE,
      isTab: this.isIEBrowser ? event.key === Keyboard.TAB : event.code === Keyboard.TAB,
      isShift: this.isIEBrowser ? event.key === Keyboard.SHIFT : event.code === Keyboard.SHIFT,
      isDelete: this.isIEBrowser ? event.key === Keyboard.DEL : event.code === Keyboard.DELETE
    };
  }

  isEmptyValue(event: KeyboardEvent, regex: RegExp): boolean {
    let newKeyboard: any = {};
    if (event.code === undefined || event.code === '' || !event.code) {
      newKeyboard = this.getKeyBoardEventCode(event);
    } else {
      newKeyboard = this.getKeyBoardEvent(event);
    }
    const keyBoard = newKeyboard;
    return (event.target as HTMLInputElement).value.length >= (event.target as HTMLInputElement).maxLength && regex.test(event.key)
      || keyBoard.isBackSpace || keyBoard.isDelete;
  }

  pasteDigitsSequence(otpCode: string[]): void {
    if (otpCode.length < 6) {
      for (let i = otpCode.length; i < 6; i++) {
        otpCode.push('a');
      }
    }
    for (let i = 0; i < otpCode.length; i++) {
      if (otpCode[i] === 'a') {
        this.verificationCodeForm.controls[`digit${ Number(i) + 1 }`].setValue('');

      } else {
        this.verificationCodeForm.controls[`digit${ Number(i) + 1 }`].setValue(otpCode[i]);
        if (i === otpCode.length - 1) {
          this.submitBtnElement.nativeElement.focus();
        }
      }
    }
  }

  onKeyPress(event: KeyboardEvent, index: number): void {
    if ((event.target as HTMLInputElement)?.value?.length > (event.target as HTMLInputElement)?.maxLength) {
      const tempValue = (event.target as HTMLInputElement)?.value.slice(-1);
      (event.target as HTMLInputElement).value = tempValue;
      this.focusNextInputBox(event, index);
    }
  }

  onValueChange(event: KeyboardEvent, index: number): void {
    this.expiredAndIncorrectError = false;
    this.systemUnavailableError = false;
    const keyBoard = this.getKeyBoardEvent(event);
    const regex = new RegExp(/^\d+$/);
    if (regex.test(event.key) || keyBoard.isRightArrow || keyBoard.isLeftArrow || keyBoard.isBackSpace || event.key === 'Unidentified') {
      const inputElements = this.el.nativeElement.querySelectorAll('input');
      /**** -----  To Focus on Next immediate input box  ---- **/
      if (((event.target as HTMLInputElement).value.length === 1 || keyBoard.isRightArrow) && !keyBoard.isLeftArrow) {
        this.focusNextInputBox(event, index);
        /****--- To Focus on Previous input box  ---- **/
      } else if ((!(event.target as HTMLInputElement).value && index && keyBoard.isBackSpace && !this.cursorClicked) ||
        (keyBoard.isLeftArrow && index >= 0)) {
        inputElements[Number(index) - 1].focus();
        inputElements[Number(index) - 1].select();
      } else {
        this.onKeyPress(event, index);
      }
    }
  }

  onClickEvent(index: number): void {
    const inputElements = this.el.nativeElement.querySelectorAll('input');
    if (inputElements[Number(index)].value) {
      this.cursorClicked = true;
    }
  }

  onBlurEvent(index: number): void {
    const validStatus = [];
    const inputElements = this.el.nativeElement.querySelectorAll('input');
    inputElements.forEach((val, i) => {
      if (i < index && !val.value) {
        this.digitsStatus[`digit${ Number(i) + 1 }`] = true;
        validStatus.push(true);
      } else {
        this.digitsStatus[`digit${ Number(i) + 1 }`] = false;
      }
    });
    this.digitValidStatus = !!validStatus.length;
    this.expiredAndIncorrectError = false;
    this.cursorClicked = false;
  }

  onKeyDown(event: KeyboardEvent, index: number): void {
    const keyBoard = this.getKeyBoardEvent(event);
    if (keyBoard.isBackSpace && (event.target as HTMLInputElement).nodeName === 'INPUT') {
      event.preventDefault();
    }
    const regex = new RegExp(/^\d+$/);
    if (this.isEmptyValue(event, regex)) {
      this.verificationCodeForm.controls[`digit${ Number(index) + 1 }`].setValue('');
    } else if (((keyBoard.isUpArrow || keyBoard.isDownArrow || !regex.test(event.key) ||
        (event.target as HTMLInputElement).value.length >= (event.target as HTMLInputElement).maxLength) &&
      (!keyBoard.isTab && !keyBoard.isShift) && !(event.ctrlKey &&
        (event.code === 'KeyV' || event.key === 'V' || event.key === 'v')))) {
      event.preventDefault();
    }
  }

  pasteValues(element: ClipboardEvent): void {
    const pasteValue = element.clipboardData || window['clipboardData'];
    const trimmedValue = pasteValue.getData('Text');
    const removeSpace = trimmedValue.replace(/\s+/g, '');
    let onlyNumber = removeSpace.match(/\d+/g);
    if (onlyNumber === null) {
      onlyNumber = [];
    } else if (Array.isArray(onlyNumber)) {
      onlyNumber = onlyNumber.join('');
      onlyNumber = onlyNumber.split('');
    }
    this.pasteDigitsSequence(onlyNumber);
    element.preventDefault();
  }

  handleChange(): void {
    const firstValue: any = this.verificationCodeForm.controls[`digit1`].value;
    const array = firstValue.toString().split('');
    if (firstValue) {
      if (array.length > 5) {
        this.pasteDigitsSequence(array);
      }
    }
  }

  navigateToCustomerSupport(): void {
    this.customerSupportUrl = `https://${this.configData.env.host}/${this.locale.iso}${this.configData.links.customerSupportUrl_alt}`;
    window.open(this.customerSupportUrl, '_blank');
  }

}
